/**
 * Known feature flags. New flags are added here so the Settings panel and
 * consumers share one catalog. DB stores only overrides; missing rows use
 * `defaultMode`.
 *
 * Modes:
 * - `enabled` — available to everyone
 * - `admins_only` — available only to Backstage admins
 * - `disabled` — unavailable to everyone, including admins
 *
 * To add a flag for another plugin:
 * 1. Append an entry below (and optionally a named constant).
 * 2. Gate UI with `<FeatureFlagGate flag={YOUR_FLAG}>` / `useFeatureFlag`.
 * 3. Gate API with `assertFeatureFlagEnabled(featureFlags, YOUR_FLAG, { isAdmin })`
 *    where `isAdmin` is admin-group membership (not the flag-update permission).
 */

export const FEATURE_FLAG_MODES = [
  'enabled',
  'admins_only',
  'disabled',
] as const;

export type FeatureFlagMode = (typeof FEATURE_FLAG_MODES)[number];

export const FEATURE_FLAG_DEFINITIONS = [
  {
    key: 'golden-paths',
    title: 'Golden Paths',
    description:
      'Show Golden Paths in the sidebar and allow access to its pages.',
    defaultMode: 'disabled' as FeatureFlagMode,
  },
  {
    key: 'my-ai-tools',
    title: 'My AI Tools',
    description:
      'Show My AI Tools in the sidebar and allow access to its pages.',
    defaultMode: 'disabled' as FeatureFlagMode,
  },
  {
    key: 'qeta-bulk-import',
    title: 'Q&A Bulk Import',
    description:
      'Show the Q&A Bulk Import button and allow access to its import APIs.',
    defaultMode: 'disabled' as FeatureFlagMode,
  },
  {
    key: 'adr',
    title: 'ADRs',
    description:
      'Show the platform ADR browse page (/adr) in the sidebar and allow access. Defaults to admins_only for controlled rollout; entity ADR tabs are not gated.',
    defaultMode: 'admins_only' as FeatureFlagMode,
  },
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_DEFINITIONS)[number]['key'];

/** Typed key for the Golden Paths runtime flag. */
export const GOLDEN_PATHS_FEATURE_FLAG: FeatureFlagKey = 'golden-paths';

/** Typed key for the My AI Tools runtime flag. */
export const MY_AI_TOOLS_FEATURE_FLAG: FeatureFlagKey = 'my-ai-tools';

/** Typed key for the Q&A Bulk Import runtime flag. */
export const QETA_BULK_IMPORT_FEATURE_FLAG: FeatureFlagKey = 'qeta-bulk-import';

/** Typed key for the platform ADR browse page. Entity ADR tabs are not gated. */
export const ADR_FEATURE_FLAG: FeatureFlagKey = 'adr';

export interface FeatureFlagDefinition {
  key: string;
  title: string;
  description: string;
  defaultMode: FeatureFlagMode;
}

export interface FeatureFlagState {
  key: string;
  title: string;
  description: string;
  mode: FeatureFlagMode;
  /** True when the value comes from DB rather than the code default. */
  isOverridden: boolean;
  updatedBy?: string;
  updatedAt?: string;
}

export interface FeatureFlagsApi {
  /** List all known flags with effective state. */
  listFlags(): Promise<FeatureFlagState[]>;
  /** Update a single flag mode (admin only). */
  setFlag(key: string, mode: FeatureFlagMode): Promise<FeatureFlagState>;
}

export function getFeatureFlagDefinition(
  key: string,
): FeatureFlagDefinition | undefined {
  return FEATURE_FLAG_DEFINITIONS.find(flag => flag.key === key);
}

export function listFeatureFlagDefinitions(): FeatureFlagDefinition[] {
  return [...FEATURE_FLAG_DEFINITIONS];
}

export function isFeatureFlagMode(value: unknown): value is FeatureFlagMode {
  return (
    typeof value === 'string' &&
    (FEATURE_FLAG_MODES as readonly string[]).includes(value)
  );
}

/** Whether a viewer may use the feature given the stored mode. */
export function isFeatureFlagAllowedForViewer(
  mode: FeatureFlagMode,
  isAdmin: boolean,
): boolean {
  if (mode === 'disabled') {
    return false;
  }
  if (mode === 'admins_only') {
    return isAdmin;
  }
  return true;
}
